// @cortex/i18n — centralised translation runtime
//
// Single source of truth for all user-facing strings. Apps import `t`,
// `useLang`, `LangStore` from here instead of maintaining local
// dictionaries. The DE locale file is authoritative; other locales
// fall back to DE → raw key when a translation is missing.

import { useEffect, useState } from 'react';
import de from './locales/de';
import { DICTIONARIES } from './server';
import {
  DEFAULT_LANG,
  type Lang,
  type TranslationKey,
  type TranslationVars,
  isSupportedLang,
} from './types';

export type { Lang, TranslationKey, TranslationVars } from './types';
export { DEFAULT_LANG, SUPPORTED_LANGS, isSupportedLang } from './types';
export { tForLang } from './server';
export {
  formatDate,
  formatRelativeDate,
  formatNumber,
  formatCurrency,
  formatPercent,
} from './formatting';

// ---------------------------------------------------------------------------
// Enabled-locales gate — controlled via VITE_ENABLED_LOCALES env var.
// Only locales in this list appear in the language switcher and are
// accepted in URL prefixes. Defaults to ['de'] when unset.
// ---------------------------------------------------------------------------

function parseEnabledLocales(): readonly Lang[] {
  try {
    const meta = import.meta as unknown as { env?: Record<string, string> };
    const raw = meta.env?.VITE_ENABLED_LOCALES ?? '';
    if (!raw) return [DEFAULT_LANG];
    const parsed = raw
      .split(',')
      .map((s: string) => s.trim().toLowerCase())
      .filter(isSupportedLang);
    return parsed.length > 0 ? parsed : [DEFAULT_LANG];
  } catch {
    return [DEFAULT_LANG];
  }
}

export const ENABLED_LOCALES: readonly Lang[] = parseEnabledLocales();

export function isEnabledLang(lang: string): lang is Lang {
  return isSupportedLang(lang) && (ENABLED_LOCALES as readonly string[]).includes(lang);
}

// ---------------------------------------------------------------------------
// LangStore — reactive language state, persisted in localStorage.
//
// Hydration priority (on first load):
//   1. Server user preference (window.__cortexUser?.language)
//   2. URL path prefix (/en/..., /fr/...)
//   3. localStorage 'ls_lang'
//   4. navigator.languages (browser locale, if supported) — a non-DE browser must
//      never land on German (launch requirement, Janik 2026-07-05)
//   5. DEFAULT_LANG ('de')
// ---------------------------------------------------------------------------

type LangStoreType = {
  get: () => Lang;
  set: (lang: Lang, opts?: { persist?: boolean }) => void;
  subscribe: (cb: () => void) => () => void;
};

function createLangStore(): LangStoreType {
  // URL prefix: only accept enabled locales (controls what's routable)
  const urlLang = (() => {
    try {
      const seg = window.location.pathname.split('/')[1];
      if (seg && isEnabledLang(seg)) return seg;
    } catch {
      /* SSR / non-browser */
    }
    return null;
  })();

  // localStorage + server preference: accept any supported lang, not just
  // enabled ones. A user who previously set 'en' shouldn't be forced back
  // to 'de' just because ENABLED_LOCALES doesn't include 'en' yet.
  const stored = (() => {
    try {
      const v = localStorage.getItem('ls_lang');
      return v && isSupportedLang(v) ? v : null;
    } catch {
      return null;
    }
  })();

  const serverLang = (() => {
    try {
      const win = window as unknown as Record<string, unknown>;
      const u = win.__cortexUser as { language?: string } | null | undefined;
      const l = u?.language;
      return l && isSupportedLang(l) ? l : null;
    } catch {
      return null;
    }
  })();

  // Browser-locale fallback (launch, Janik): a brand-new non-DE visitor with no
  // session, no /xx prefix and empty ls_lang must NOT land on German. Consulted
  // ONLY below the three real signals and ABOVE the hardcoded DEFAULT_LANG. Tags
  // carry region subtags ('fr-FR') while SUPPORTED_LANGS holds bare codes → base-
  // match. Returns null on no match so the ?? chain preserves DEFAULT_LANG. Feeds
  // ONLY `current` — never the ls_lang mirror below — so it can't be persisted.
  const navigatorLang = ((): Lang | null => {
    try {
      const langs = navigator.languages?.length ? navigator.languages : [navigator.language];
      for (const raw of langs) {
        const base = raw.toLowerCase().split(/[-_]/)[0];
        if (isSupportedLang(base)) return base;
      }
    } catch {
      /* SSR / non-browser */
    }
    return null;
  })();

  let current: Lang = serverLang ?? urlLang ?? stored ?? navigatorLang ?? DEFAULT_LANG;

  // LEA-230: sync localStorage when server preference differs
  if (urlLang && stored !== urlLang) {
    try {
      localStorage.setItem('ls_lang', urlLang);
    } catch {
      /* noop */
    }
  }
  if (serverLang && stored !== serverLang) {
    try {
      localStorage.setItem('ls_lang', serverLang);
    } catch {
      /* noop */
    }
  }

  const listeners = new Set<() => void>();

  return {
    get: () => current,
    // `persist` defaults to true: an explicit user choice writes ls_lang and
    // syncs the server via __saveLanguage. Pass { persist: false } for a
    // display-only default — e.g. the LEA-339 English auth-page fallback —
    // that must NOT mutate localStorage or PATCH the server. A persisting
    // auto-default would otherwise (a) stick app-wide on the next load and
    // (b) for a visitor who still holds a session cookie, silently overwrite
    // their saved server language.
    set: (l: Lang, opts?: { persist?: boolean }) => {
      current = l;
      if (opts?.persist !== false) {
        try {
          localStorage.setItem('ls_lang', l);
        } catch {
          /* noop */
        }
        if (typeof window !== 'undefined') {
          const win = window as unknown as Record<string, unknown>;
          if (typeof win.__saveLanguage === 'function') {
            (win.__saveLanguage as (lang: string) => void)(l);
          }
        }
      }
      for (const cb of listeners) cb();
    },
    subscribe: (cb: () => void) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
  };
}

export const LangStore: LangStoreType =
  typeof window !== 'undefined'
    ? createLangStore()
    : { get: () => DEFAULT_LANG, set: () => {}, subscribe: () => () => {} };

// ---------------------------------------------------------------------------
// t() — translate a key, with optional variable interpolation.
//
// Fallback chain: target locale → DE → '[key]' (bracket-wrapped for
// visual spotting during QA).
// ---------------------------------------------------------------------------

export function t(key: TranslationKey | string, vars?: TranslationVars): string {
  const lang = LangStore.get();
  const dict = DICTIONARIES[lang];
  // LEA-261: use `??` instead of `||` — intentionally empty strings are valid
  let s = dict?.[key] ?? (de as Record<string, string>)[key] ?? `[${key}]`;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      // LEA-418: ALLE Vorkommen ersetzen. String.replace() mit String-Pattern
      // tauscht nur das ERSTE aus — bei Strings mit doppeltem Platzhalter blieb
      // das zweite als literales '{n}' sichtbar (dash.run.cost.base in allen 7
      // Locales, nav.runs.leadsReady in es/fr). split/join statt replaceAll:
      // kein $-Sonderzeichen-Handling im Replacement.
      s = s.split(`{${k}}`).join(String(v));
    }
  }
  return s;
}

// tForLang() lives in ./server (re-exported above) so backend packages can
// import it without pulling React.

// ---------------------------------------------------------------------------
// useLang() — React hook that re-renders on language change.
// ---------------------------------------------------------------------------

export function useLang(): Lang {
  const [, force] = useState(0);
  useEffect(() => LangStore.subscribe(() => force((n) => n + 1)), []);
  return LangStore.get();
}

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

export function getLangFromPath(path: string): Lang {
  const seg = path.split('/')[1];
  if (seg && isEnabledLang(seg)) return seg;
  return DEFAULT_LANG;
}

export function swapLangInPath(path: string, newLang: Lang): string {
  const segments = path.split('/');
  if (segments[1] && isSupportedLang(segments[1])) {
    if (newLang === DEFAULT_LANG) {
      segments.splice(1, 1);
    } else {
      segments[1] = newLang;
    }
  } else if (newLang !== DEFAULT_LANG) {
    segments.splice(1, 0, newLang);
  }
  return segments.join('/') || '/';
}

export function localePath(path: string, lang?: Lang): string {
  const l = lang ?? LangStore.get();
  if (l === DEFAULT_LANG) return path;
  return `/${l}${path.startsWith('/') ? path : `/${path}`}`;
}

// ---------------------------------------------------------------------------
// Expose on window for prototype Babel scripts (backwards compat)
// ---------------------------------------------------------------------------

if (typeof window !== 'undefined') {
  const win = window as unknown as Record<string, unknown>;
  win.I18N = DICTIONARIES;
  win.LangStore = LangStore;
  win.useLang = useLang;
  win.t = t;
}
